// Copyright (c) 2022 by Move Closer

import { Module } from 'vuex'

import { CartIdentifier } from '../../contexts/checkout/contracts'

import { State } from '../shared/contracts/store'
import { CheckoutState, MutationType } from './contracts'

import { CartModel } from './contracts/models'
import { CartBundleItem } from '../../contexts/checkout/contracts/bundles'
import { AirportScheduleItem } from '../../contexts/checkout/contracts/flight-data'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const storeFactory = (): Module<CheckoutState, State> => ({
  namespaced: true,

  state: () => ({
    id: null,
    cart: null,
    loading: false,
    isCartMergerPending: false,
    registering: false,
    ssoAuthenticated: false,
    ssoConsentsRequest: false,
    presaleCartItemsPresent: false,
    onlyRegularCartItemsPresent: false,
    isBundlePresale: false,
    isPresaleBundleInCartItems: false,
    bundles: [],
    flightsSchedule: []
  }),

  mutations: {
    [MutationType.SetCartId] (state, cartId: CartIdentifier): void {
      state.id = cartId
    },
    [MutationType.SetCart] (state, cart: CartModel): void {
      state.cart = cart
    },
    [MutationType.SetLoading] (state, loading: boolean): void {
      state.loading = loading
    },
    [MutationType.SetIsCartMergerPending] (state, value: boolean): void {
      state.isCartMergerPending = value
    },
    [MutationType.SetRegisteringInCheckout] (state, registering: boolean): void {
      state.registering = registering
    },
    [MutationType.SetSSOAuthenticated] (state, authenticated: boolean): void {
      state.ssoAuthenticated = authenticated
    },
    [MutationType.SetSSOConsentsRequest] (state, value: boolean): void {
      state.ssoConsentsRequest = value
    },
    [MutationType.SetPresaleCartItemsPresent] (state, value: boolean): void {
      state.presaleCartItemsPresent = value
    },
    [MutationType.SetOnlyRegularCartItemsPresent] (state, value: boolean): void {
      state.onlyRegularCartItemsPresent = value
    },
    [MutationType.SetIsBundlePresale] (state, value: boolean): void {
      state.isBundlePresale = value
    },
    [MutationType.SetIsPresaleBundleInCartItems] (state, value: boolean): void {
      state.isPresaleBundleInCartItems = value
    },
    [MutationType.SetBundles] (state, bundles: Array<CartBundleItem>): void {
      state.bundles = bundles
    },
    [MutationType.SetFlightsSchedule] (state, items: Array<AirportScheduleItem>): void {
      state.flightsSchedule = items
    }
  },

  getters: {
    /**
     * Returns the identifier of cart.
     */
    cartId: (state) => state.id,

    /**
     * Returns the cart model.
     */
    cart: (state) => state.cart,

    /**
     * Returns if cart is loading.
     */
    loading: (state) => state.loading,

    /**
     * Returns of cart merger is pending
     */
    isCartMergerPending: (state) => state.isCartMergerPending,

    /**
     * Returns whether user is in progress of registering their account during checkout.
     */
    registering: (state) => state.registering,

    /**
     * Returns whether user has been successfully authenticated with SSO
     */
    ssoAuthenticated: (state) => state.ssoAuthenticated,

    /**
     * Retrieves a state o SSO authentication when user has to accept consents
     */
    ssoConsentsRequest: (state) => state.ssoConsentsRequest,

    /**
     * Retrieves information whether at least one PRESALE item is in cart
     */
    presaleCartItemsPresent: (state) => state.presaleCartItemsPresent,

    /**
     * Retrieves information whether all cart items are regular = not PRESALE
     */
    onlyRegularCartItemsPresent: (state) => state.onlyRegularCartItemsPresent,

    /**
     * Retrieves information if bundle product's children are presale
     */
    isBundlePresale: (state) => state.isBundlePresale,

    /**
     * Retrieves information if cart has bundle item and that item is in presale
     */
    isPresaleBundleInCartItems: (state) => state.isPresaleBundleInCartItems,

    /**
     * Retrieves cart bundle items
     */
    bundles: (state) => state.bundles,

    /**
     * Retrieves a list of schedules airport flights
     */
    flightsSchedule: (state) => state.flightsSchedule
  }
})
